import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { HERO_IMAGE, HERO_VIDEO, HERO_VIDEO_WEBM } from "@/data/content";
import { UI } from "@/i18n/copy";
import { useLang } from "@/i18n/LanguageContext";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="-mt-4 block overflow-hidden px-1 pb-4 pt-4">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { lang } = useLang();
  const t = UI[lang].hero;
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section ref={ref} id="home" data-testid="hero-section" className="relative flex min-h-screen flex-col overflow-hidden bg-black">
      <motion.div style={{ y: imgY }} className="absolute inset-0">
        <motion.video
          data-testid="hero-video"
          initial={{ scale: 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease: EASE }}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster={HERO_IMAGE}
          disablePictureInPicture
          aria-hidden="true"
          className="h-full w-full object-cover"
        >
          <source src={HERO_VIDEO} type="video/mp4" />
          <source src={HERO_VIDEO_WEBM} type="video/webm" />
        </motion.video>
      </motion.div>
      <div className="absolute inset-0 bg-black/25" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/55 to-black/40" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent" />

      <motion.div
        style={{ opacity: contentOpacity, y: contentY }}
        className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-end px-5 pb-20 pt-40 sm:px-8"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mb-8 flex items-center gap-4"
          data-testid="hero-eyebrow"
        >
          <span className="h-px w-12 bg-white/60" />
          <span className="font-arabic text-sm text-white/80">مجموعة آل طالب</span>
          <span className="font-mono-label text-[11px] uppercase tracking-[0.3em] text-white/60">
            {t.city}
          </span>
        </motion.div>

        <h1 className="max-w-5xl text-[13vw] font-black uppercase leading-[0.95] tracking-tight text-white sm:text-7xl lg:text-8xl xl:text-[7.5rem]">
          <MaskedLine delay={0.35}>{t.line1}</MaskedLine>
          <MaskedLine delay={0.5}>
            {lang === "en" ? (
              <span className="font-script normal-case leading-[1.2] tracking-normal">{t.line2}</span>
            ) : (
              t.line2
            )}
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
          className="mt-8 max-w-xl text-base leading-relaxed text-white/75 sm:text-lg"
          data-testid="hero-subtext"
        >
          {t.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-explore"
            onClick={() => scrollToId("companies")}
            className="group inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-[13px] font-bold uppercase tracking-[0.14em] text-black transition-colors duration-300 hover:bg-white/85"
          >
            {t.ctaPrimary}
            <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
          </button>
          <button
            data-testid="hero-cta-contact"
            onClick={() => scrollToId("contact")}
            className="group inline-flex items-center gap-2 rounded-full border border-white/40 px-8 py-4 text-[13px] font-bold uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:border-white hover:bg-white/10"
          >
            {t.ctaSecondary}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 rtl:-scale-x-100" />
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 0.8 }}
        className="relative z-10 mx-auto flex w-full max-w-7xl items-center justify-between border-t border-white/15 px-5 py-5 sm:px-8"
      >
        <span className="font-mono-label text-[11px] uppercase tracking-[0.25em] text-white/50">
          {t.foot}
        </span>
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          className="hidden items-center gap-2 font-mono-label text-[11px] uppercase tracking-[0.25em] text-white/50 sm:flex"
        >
          {t.scroll} <ArrowDown className="h-3.5 w-3.5" />
        </motion.span>
      </motion.div>
    </section>
  );
};
